import * as React from 'react';
import './Chart.css';
import { useSelector } from 'react-redux';
import { selectGraphResults, selectGraphSpinner } from '../../selectors';
import Flex from '../../../../components/Flex/Flex';
import Loader from '../../../../components/Loader/Loader';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  YAxis,
} from 'recharts';
import formatWinnings from '../../../../utils/formatWinnings';
import Text from '../../../../components/Text/Text';

const SMOOTH_LINE = {
  type: 'natural' as const,
  dot: false,
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const Chart = React.memo(() => {
  const graphResults = useSelector(selectGraphResults);
  const loading = useSelector(selectGraphSpinner);

  if (loading) {
    return (
      <Flex className="Chart__Loading" justify="center" align="center">
        <Loader />
      </Flex>
    );
  }

  const gr = graphResults
    ? graphResults
    : { result: [], resultWithShowdown: [], resultWithoutShowdown: [] };

  const { result } = gr;

  if (result.length === 0) {
    return (
      <Flex className="Chart" justify="center" align="center">
        <Text>-</Text>
      </Flex>
    );
  }

  const data = result.reduce<
    Array<{
      result: number;
      resultWithShowdown: number;
      resultWithoutShowdown: number;
    }>
  >(
    (acc, _res, index) => {
      acc.push({
        result: acc[acc.length - 1].result + gr.result[index],
        resultWithShowdown: acc[acc.length - 1].resultWithShowdown + gr.resultWithShowdown[index],
        resultWithoutShowdown:
          acc[acc.length - 1].resultWithoutShowdown + gr.resultWithoutShowdown[index],
      });
      return acc;
    },
    [{ result: 0, resultWithShowdown: 0, resultWithoutShowdown: 0 }],
  );

  return (
    <div className="Chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <CartesianGrid
            vertical={false}
            opacity={0.6}
            stroke="var(--color-text-tertiary)"
            strokeDasharray="3 3"
          />
          <YAxis
            type="number"
            orientation="right"
            stroke="var(--color-text-tertiary)"
            opacity={0.6}
            strokeWidth={0.5}
            tickLine={false}
            tickCount={7}
            tickFormatter={(x) => formatWinnings(x, 2, true)}
            tick={{
              stroke: 'var(--color-text-tertiary)',
              opacity: 1,
              fontSize: 12,
              fontWeight: 100,
              strokeWidth: 1,
            }}
          />

          <Line {...SMOOTH_LINE} dataKey="resultWithShowdown" stroke="var(--color-primary)" />
          <Line {...SMOOTH_LINE} dataKey="resultWithoutShowdown" stroke="var(--color-red-6)" />
          <Line {...SMOOTH_LINE} dataKey="result" stroke="var(--color-green-6)" />
          <ReferenceLine y={0} stroke="white" strokeWidth={1.5} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

export default Chart;
